import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from 'components/ui/MainSidebar';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import PortfolioHeader from './components/PortfolioHeader';
import HoldingsTable from './components/HoldingsTable';
import PerformanceChart from './components/PerformanceChart';
import AllocationChart from './components/AllocationChart';
import WatchlistTab from './components/WatchlistTab';
import ROICalculator from './components/ROICalculator';
import AddStockModal from './components/AddStockModal';


const INITIAL_HOLDINGS = [
  { ticker: 'AAPL', name: 'Apple Inc.', sector: 'Technology', shares: 25, avgBuy: 155.40, currentPrice: 189.30, changePercent: 2.14, gainLoss: 847.50 },
  { ticker: 'MSFT', name: 'Microsoft Corporation', sector: 'Technology', shares: 15, avgBuy: 310.20, currentPrice: 415.20, changePercent: 1.87, gainLoss: 1575.00 },
  { ticker: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer', shares: 10, avgBuy: 178.50, currentPrice: 198.45, changePercent: -0.63, gainLoss: 199.50 },
  { ticker: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', shares: 20, avgBuy: 165.80, currentPrice: 152.30, changePercent: -1.22, gainLoss: -270.00 },
  { ticker: 'JPM', name: 'JPMorgan Chase & Co.', sector: 'Finance', shares: 12, avgBuy: 172.40, currentPrice: 198.75, changePercent: 0.94, gainLoss: 316.20 },
  { ticker: 'V', name: 'Visa Inc.', sector: 'Finance', shares: 18, avgBuy: 245.60, currentPrice: 278.90, changePercent: 1.45, gainLoss: 599.40 },
  { ticker: 'TSLA', name: 'Tesla Inc.', sector: 'Automotive', shares: 8, avgBuy: 280.00, currentPrice: 248.60, changePercent: -2.31, gainLoss: -251.20 },
];

const PORTFOLIO_DATA = {
  totalValue: 48750.25,
  investedAmount: 42380.00,
  totalROI: 15.03,
  todayGain: 387.45,
  todayGainPercent: 0.80,
};

const TABS = [
  { id: 'holdings', label: 'Holdings', icon: 'BarChart2' },
  { id: 'watchlist', label: 'Watchlist', icon: 'Star' },
  { id: 'calculator', label: 'ROI Calculator', icon: 'Calculator' },
];

export default function PortfolioTracker() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('holdings');
  const [holdings, setHoldings] = useState(INITIAL_HOLDINGS);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddStock = (newStock) => {
    const gainLoss = (newStock?.currentPrice - newStock?.avgBuy) * newStock?.shares;
    const changePercent = ((newStock?.currentPrice - newStock?.avgBuy) / newStock?.avgBuy) * 100;
    setHoldings(prev => [...prev, { ...newStock, gainLoss, changePercent }]);
  };

  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-background)' }}>
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(c => !c)} />
      {/* Main Content */}
      <main className={`flex-1 min-w-0 transition-all duration-250 pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[240px]'}`}>

        {/* Top Header Bar */}
        <header className="sticky top-0 z-40 flex items-center justify-between px-4 md:px-6 lg:px-8 border-b"
          style={{ height: 'var(--header-height)', background: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3 ml-12 lg:ml-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(133,113,77,0.1)' }}>
              <Icon name="PieChart" size={18} color="var(--color-accent)" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-base md:text-lg" style={{ color: 'var(--color-foreground)' }}>Portfolio Tracker</h1>
              <p className="font-caption text-xs hidden sm:block" style={{ color: 'var(--color-muted-foreground)' }}>Last updated: Mar 7, 2026 · 2:24 AM EST</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-caption text-xs font-medium transition-colors hover:bg-muted"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)' }}>
              <Icon name="Download" size={14} color="currentColor" />
              Export
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg border transition-colors hover:bg-muted"
              style={{ borderColor: 'var(--color-border)' }}>
              <Icon name="Bell" size={18} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </header>

        <div className="px-4 md:px-6 lg:px-8 py-6">
          {/* Portfolio Header */}
          <PortfolioHeader portfolioData={PORTFOLIO_DATA} />

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
            <div className="lg:col-span-2">
              <PerformanceChart />
            </div>
            <div className="lg:col-span-1">
              <AllocationChart />
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-1 overflow-x-auto">
              {TABS?.map(tab => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                  className="flex items-center gap-2 px-4 py-3 font-caption text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 flex-shrink-0"
                  style={{
                    borderColor: activeTab === tab?.id ? 'var(--color-accent)' : 'transparent',
                    color: activeTab === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                  }}>
                  <Icon name={tab?.icon} size={15} color="currentColor" strokeWidth={activeTab === tab?.id ? 2 : 1.75} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="tab-content-enter">
            {activeTab === 'holdings' && (
              <HoldingsTable holdings={holdings} onAddStock={() => setShowAddModal(true)} />
            )}
            {activeTab === 'watchlist' && <WatchlistTab />}
            {activeTab === 'calculator' && <ROICalculator />}
          </div>

          {/* Quick Nav */}
          <div className="mt-8 pt-6 border-t flex flex-wrap gap-3" style={{ borderColor: 'var(--color-border)' }}>
            <Button variant="outline" size="sm" iconName="TrendingUp" iconPosition="left" onClick={() => navigate('/investment-opportunities')}>
              Browse Opportunities
            </Button>
            <Button variant="outline" size="sm" iconName="LayoutDashboard" iconPosition="left" onClick={() => navigate('/home-dashboard')}>
              Dashboard
            </Button>
            <Button variant="outline" size="sm" iconName="Users" iconPosition="left" onClick={() => navigate('/community-hub')}>
              Community
            </Button>
          </div>
        </div>
      </main>
      {/* Add Stock Modal */}
      {showAddModal && (
        <AddStockModal onClose={() => setShowAddModal(false)} onAdd={handleAddStock} />
      )}
    </div>
  );
}